import { QualityChoiceModal } from "@components/quality-choice-modal";
import { UpgradeToHighQualityButton } from "@components/upgrade-to-high-quality-button";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { usePacks } from "@features/images/use-packs";
import { useLoras } from "@features/loras/use-loras";
import { useStudio } from "@features/studio/use-studio";
import { useEffectPlease, useNavigateAlt } from "@features/utils";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { Heading, IconButton, Spinner } from "@radix-ui/themes";
import { getPreviousPath } from "@views/layout/layout";
import { SideBarContainer } from "@views/layout/sidebar";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  StudioBackground,
  StudioCreateVideo,
  StudioCropExtend,
  StudioEditObjects,
  StudioInformation,
  StudioPhotoEffects,
  StudioRemix,
  StudioUpscale,
  StudioTryOn,
  StudioHaircut,
  StudioFace,
} from "./actions";
import { StudioActionGrid } from "./studio-action-grid";
import { shouldShowActionGrid } from "./studio-actions-utils";

// Valid tool names for URL hash
const VALID_TOOLS = [
  "information",
  "crop-extend",
  "photo-effects",
  "edit-objects",
  "background",
  "create-video",
  "upscale",
  "remix",
  "try-on",
  "haircut",
  "face",
];

interface StudioSidebarProps {
  image: ImagesType;
  changeTool: (tool: string) => void;
  closestImageId?: string;
}

export const StudioSidebar = ({
  image,
  changeTool,
  closestImageId,
}: StudioSidebarProps) => {
  const { setStudio } = useStudio();
  const { loras } = useLoras();
  const { packs } = usePacks();
  const { pendingUpdate } = useImage(image.id);
  const navigate = useNavigateAlt();
  const { site } = useParams<{ site?: string }>();
  const [showQualityModal, setShowQualityModal] = useState(false);

  // Locked actions when user has no loras (free mode)
  const lockedActions = !loras.data?.length
    ? ["background", "crop-extend", "create-video", "try-on", "haircut", "face"]
    : [];

  // Find the first low-quality avatar model for upgrade path
  const lowQualityAvatar = loras.data?.find(
    (lora) =>
      lora.technology === "images" &&
      lora.subtype === "avatar" &&
      lora.status === "completed"
  );

  const handleAddMorePictures = () => {
    navigate(
      lowQualityAvatar?.id
        ? `/${site || "portraits"}/lora/${lowQualityAvatar?.id}/upgrade`
        : `/${site || "portraits"}/lora/model`
    );
    setShowQualityModal(false);
  };

  const handleUpgrade = () => {
    navigate(`/billing`);
    setShowQualityModal(false);
  };

  // Get initial tool from URL hash, default to "information"
  const getToolFromHash = () => {
    const hash = window.location.hash.slice(1); // Remove the # symbol
    return VALID_TOOLS.includes(hash) ? hash : "information";
  };

  const [selectedAction, setSelectedAction] = useState<string>(getToolFromHash);

  // Update URL hash when tool changes
  useEffect(() => {
    const newHash =
      selectedAction === "information" ? "" : `#${selectedAction}`;
    const currentHash = window.location.hash;
    if (newHash !== currentHash) {
      window.history.replaceState(
        null,
        "",
        newHash || window.location.pathname + window.location.search
      );
    }
  }, [selectedAction]);

  // Listen for hash changes (e.g., browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const toolFromHash = getToolFromHash();
      if (toolFromHash !== selectedAction) {
        setSelectedAction(toolFromHash);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [selectedAction]);

  useEffectPlease(() => {
    setStudio({
      tool: selectedAction,
      is_editing:
        selectedAction === "crop-extend" ||
        selectedAction === "photo-effects" ||
        selectedAction === "edit-objects",
      // Default to chips mode when entering edit-objects
      ...(selectedAction === "edit-objects" ? { edit_tool: "chips" } : {}),
    });
    changeTool(selectedAction);
  }, [selectedAction]);

  const model = loras.data?.find((a) => a.id === image?.steps[0].model_lora);
  const style = loras.data?.find((a) => a.id === image?.steps[0].style_lora);
  const pack = packs.data?.find((a) => a.id === image?.pack_id);

  const handleAction = (action: string) => {
    setSelectedAction(action);
  };

  const renderSelectedComponent = () => {
    const commonProps = {
      image,
      onAction: handleAction,
    };

    switch (selectedAction) {
      case "crop-extend":
        return <StudioCropExtend {...commonProps} />;
      case "photo-effects":
        return <StudioPhotoEffects {...commonProps} />;
      case "edit-objects":
        return <StudioEditObjects {...commonProps} />;
      case "background":
        return <StudioBackground {...commonProps} />;
      case "create-video":
        return <StudioCreateVideo {...commonProps} />;
      case "upscale":
        return <StudioUpscale {...commonProps} />;
      case "remix":
        return <StudioRemix {...commonProps} />;
      case "try-on":
        return <StudioTryOn {...commonProps} />;
      case "haircut":
        return <StudioHaircut {...commonProps} />;
      case "face":
        return <StudioFace {...commonProps} />;
      case "information":
      default:
        return (
          <StudioInformation
            image={image}
            model={model}
            style={style}
            pack={pack}
            closestImageId={closestImageId}
          />
        );
    }
  };

  return (
    <SideBarContainer>
      <div className="h-4" />

      <UpgradeToHighQualityButton className={"mb-4"} />

      <div className="pb-4 space-x-2 items-center hidden md:flex">
        <Link to={getPreviousPath()}>
          <IconButton size="1" variant="soft" color="gray">
            <ArrowLeftIcon className="h-4 w-4" />
          </IconButton>
        </Link>
        <Heading size="2" className="inline-block grow">
          Studio
        </Heading>
        {pendingUpdate && <Spinner />}
      </div>

      {image.status === "completed" && (
        <div className="space-y-4">
          {shouldShowActionGrid(image) && (
            <StudioActionGrid
              onAction={handleAction}
              selectedAction={selectedAction}
              lockedActions={lockedActions}
              onLockedClick={() => setShowQualityModal(true)}
            />
          )}
          {renderSelectedComponent()}
        </div>
      )}

      {image.status === "pending" && (
        <div className="space-y-4 h-full flex flex-col items-center justify-center">
          <Spinner className="mx-auto" />
          <Heading size="2" className="text-center">
            Creating image...
          </Heading>
        </div>
      )}

      {image.status === "failed" && (
        <div className="space-y-4 h-full flex flex-col items-center justify-center">
          <XMarkIcon className="mx-auto w-8 h-8" />
          <Heading size="2" className="text-center">
            Failed to create image
          </Heading>
        </div>
      )}
      <div className="mt-4" />

      <QualityChoiceModal
        isOpen={showQualityModal}
        onClose={() => setShowQualityModal(false)}
        onContinueStandard={() => setShowQualityModal(false)}
        onAddMorePictures={handleAddMorePictures}
        onUpgrade={handleUpgrade}
      />
    </SideBarContainer>
  );
};
